import memoize from "@-/com.npmjs.memoize";
import { onlineManager } from "@-/com.npmjs.tanstack__react-query";
import { useSyncExternalStore, useMemo } from "react";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { getInstalledLevelPacks, getLevelPackIdentity } from "#level-packs";

export type ContentAvailability = {
  graphemes: ReadonlySet<string>;
  isOnline: boolean;
  packIdentity: string;
  words: ReadonlySet<string>;
};
const subscribeOnline = (listener: () => void) => onlineManager.subscribe(listener);
const getOnline = () => onlineManager.isOnline();

const getPackCoverage = memoize((_identity: string) => {
  const packs = getInstalledLevelPacks();
  return {
    graphemes: new Set(packs.flatMap(([, pack]) => pack.graphemes)),
    words: new Set(packs.flatMap(([, pack]) => pack.words)),
  };
});

export function getContentAvailability(): ContentAvailability {
  const packIdentity = getLevelPackIdentity();
  return { isOnline: onlineManager.isOnline(), packIdentity, ...getPackCoverage(packIdentity) };
}

export function useContentAvailability(): ContentAvailability {
  const [installed] = useDeviceSetting("installedLevelPacks");
  const online = useSyncExternalStore(subscribeOnline, getOnline, getOnline);
  const packIdentity = getLevelPackIdentity(installed);
  return useMemo(
    () => ({ isOnline: online, packIdentity, ...getPackCoverage(packIdentity) }),
    [packIdentity, online],
  );
}

function formatPackDataIdentity({
  isOnline,
  packIdentity,
}: Pick<ContentAvailability, "isOnline" | "packIdentity">): string {
  return `${isOnline ? "online" : "packs"}:${packIdentity}`;
}

export function getPackDataIdentity(): string {
  return formatPackDataIdentity({
    isOnline: onlineManager.isOnline(),
    packIdentity: getLevelPackIdentity(),
  });
}

/**
 * `getPackDataIdentity`, derived from the subscribed availability so it follows connectivity and
 * pack changes. Calling the zero-argument getter in a hook instead would be memoized by the React
 * Compiler, freezing the identity a component first rendered with.
 */
export function usePackDataIdentity(): string {
  return formatPackDataIdentity(useContentAvailability());
}
